import React, { useState, useRef, useEffect } from 'react';
import * as tus from 'tus-js-client';
import Header from '../components/Header';
import SessionTile from '../components/SessionTile';
import { authHeaders } from '../utils/authService';
import { formatLapTime } from '../utils/format';

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--color-bg)',
  },
  content: {
    maxWidth: 700,
    margin: '0 auto',
    padding: '32px 24px',
  },
  title: {
    fontFamily: 'var(--font-heading)',
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 2,
    marginBottom: 24,
  },
  card: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 12,
    padding: 24,
    marginBottom: 24,
  },
  dropZone: {
    border: '2px dashed var(--color-border)',
    borderRadius: 12,
    padding: '48px 24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background 0.2s',
  },
  dropZoneActive: {
    borderColor: 'var(--color-accent)',
    background: 'rgba(46, 204, 113, 0.05)',
  },
  dropLabel: {
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    color: 'var(--color-text-dim)',
    marginBottom: 8,
  },
  dropHint: {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: 'var(--color-text-dim)',
    opacity: 0.6,
  },
  fileInput: {
    display: 'none',
  },
  selectedFile: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text)',
    marginTop: 12,
  },
  uploadBtn: {
    padding: '10px 24px',
    background: 'var(--color-accent)',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    fontFamily: 'var(--font-heading)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: 1,
    marginTop: 16,
  },
  uploadBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  progress: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text-dim)',
    marginTop: 12,
  },
  error: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: '#e74c3c',
    marginTop: 12,
    padding: '8px 12px',
    background: 'rgba(231, 76, 60, 0.1)',
    borderRadius: 6,
  },
  sectionTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--color-secondary)',
    letterSpacing: 1,
    marginBottom: 12,
  },
  lapTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
  },
  lapTh: {
    textAlign: 'left',
    padding: '6px 12px',
    borderBottom: '1px solid var(--color-border)',
    color: 'var(--color-text-dim)',
    fontSize: 11,
    fontFamily: 'var(--font-heading)',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  lapTd: {
    padding: '6px 12px',
    borderBottom: '1px solid var(--color-border)',
    color: 'var(--color-text)',
  },
  lapBest: {
    color: 'var(--color-accent)',
    fontWeight: 600,
  },
  fieldsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
  },
  fieldTag: {
    padding: '3px 8px',
    background: 'rgba(74, 106, 126, 0.2)',
    borderRadius: 4,
    fontSize: 11,
    fontFamily: 'var(--font-body)',
    color: 'var(--color-text-dim)',
  },
  actionRow: {
    display: 'flex',
    gap: 12,
    marginTop: 24,
  },
  acceptBtn: {
    padding: '12px 32px',
    background: 'var(--color-accent)',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    fontFamily: 'var(--font-heading)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: 1,
    flex: 1,
  },
  rejectBtn: {
    padding: '12px 32px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-heading)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: 1,
    flex: 1,
  },
  successCard: {
    background: 'rgba(46, 204, 113, 0.1)',
    border: '1px solid var(--color-accent)',
    borderRadius: 12,
    padding: 24,
    textAlign: 'center',
  },
  successTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--color-accent)',
    marginBottom: 8,
  },
  successDetail: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text)',
    marginBottom: 4,
  },
  offsetRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  offsetInput: {
    width: 64,
    padding: '8px 10px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    textAlign: 'center',
  },
  offsetSep: {
    fontFamily: 'var(--font-body)',
    fontSize: 18,
    color: 'var(--color-text-dim)',
    fontWeight: 600,
  },
  offsetLabel: {
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    color: 'var(--color-text-dim)',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginLeft: 4,
  },
  offsetHelp: {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: 'var(--color-text-dim)',
    marginTop: 8,
  },
};

const DEFAULT_EXTENSIONS = ['.csv', '.ld', '.xrk', '.vbo', '.fit'];

export default function ImportPage({ user, onLogout, onDashboard, onAdmin, onProfile, onBack, embedded }) {
  const allowVideoUpload = user?.permissions?.allowVideoUpload === true;
  const isEmbedded = embedded === true;
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [supportedFormats, setSupportedFormats] = useState(null);
  const [allowedExtensions, setAllowedExtensions] = useState(DEFAULT_EXTENSIONS);

  useEffect(() => {
    fetch('/api/import/formats', { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setSupportedFormats(data.formats || []);
          if (data.extensions?.length) setAllowedExtensions(data.extensions);
        }
      })
      .catch(() => {});
  }, []);
  const [preview, setPreview] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [result, setResult] = useState(null);
  const [sessionType, setSessionType] = useState('');
  const [selectedTrackId, setSelectedTrackId] = useState('');
  const videoInputRef = useRef(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoDragActive, setVideoDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoOffsetH, setVideoOffsetH] = useState(0);
  const [videoOffsetM, setVideoOffsetM] = useState(0);
  const [videoOffsetS, setVideoOffsetS] = useState(0);

  const videoSyncOffsetSec = videoOffsetH * 3600 + videoOffsetM * 60 + videoOffsetS;

  const handleVideoFileSelect = (file) => {
    if (!file) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (ext !== '.mp4' && ext !== '.mov') {
      setError(`Unsupported video type: ${ext}. Only .mp4 and .mov are accepted.`);
      return;
    }
    setError(null);
    setVideoFile(file);
  };

  const handleVideoDrop = (e) => {
    e.preventDefault();
    setVideoDragActive(false);
    if (e.dataTransfer.files?.[0]) handleVideoFileSelect(e.dataTransfer.files[0]);
  };

  const handleFileSelect = (file) => {
    setError(null);
    setPreview(null);
    setResult(null);
    if (!file) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      setError(`Unsupported file type: ${ext}. Supported: ${allowedExtensions.join(', ')}`);
      return;
    }
    setSelectedFile(file);
    // Auto-upload immediately
    uploadFile(file);
  };

  const uploadFile = async (file) => {
    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const data = await new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/import/upload');
        const token = localStorage.getItem('rv_token');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          try {
            const resp = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) resolve(resp);
            else reject(new Error(resp.error || resp.detail || 'Upload failed'));
          } catch (e) { reject(new Error('Upload failed')); }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(formData);
      });

      setPreview(data);
      setSessionType(data.metadata?.sessionType || '');
      setSelectedTrackId(data.metadata?.trackId || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleAccept = async () => {
    if (!preview || accepting) return;
    setAccepting(true);
    setError(null);

    try {
      const res = await fetch('/api/import/accept', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ outputFile: preview.outputFile, sessionType, trackOverride: selectedTrackId || undefined }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Accept failed');
      }

      // Upload video if provided — resumable, parallel-chunked via tus.io
      if (videoFile && data.fileName) {
        if (!allowVideoUpload) {
          // Permission gate: the video-upload feature is behind a per-user
          // flag. Skip the network call entirely and note it on the result.
          data.videoSkipped = true;
        } else {
        setVideoUploading(true);
        setVideoProgress(0);
        try {
          await new Promise((resolve, reject) => {
            const upload = new tus.Upload(videoFile, {
              endpoint: '/api/uploads/tus',
              retryDelays: [0, 1000, 3000, 5000, 10000, 30000],
              chunkSize: 16 * 1024 * 1024,
              parallelUploads: 4,
              headers: {
                Authorization: `Bearer ${localStorage.getItem('rv_token')}`,
              },
              metadata: {
                sessionFilename: data.fileName,
                originalFilename: videoFile.name,
                videoSyncOffsetSec: String(videoSyncOffsetSec),
              },
              onError: (err) => reject(err),
              onProgress: (sent, total) => {
                if (total) setVideoProgress(Math.round((sent / total) * 100));
              },
              onSuccess: () => resolve(),
            });
            upload.start();
          });
        } catch (videoErr) {
          data.videoError = videoErr.message || 'Video upload failed';
        } finally {
          setVideoUploading(false);
        }
        }
      }

      setResult(data);

      // Notify embedded desktop app that import completed
      if (isEmbedded) {
        try {
          const f = document.createElement('iframe');
          f.style.display = 'none';
          f.src = 'racevoice://import-done';
          document.body.appendChild(f);
          setTimeout(() => f.remove(), 100);
        } catch (_) { /* ignore in non-embedded contexts */ }
      }

      setPreview(null);
      setSelectedFile(null);
      setVideoFile(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!preview) return;

    try {
      await fetch('/api/import/reject', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ outputFile: preview.outputFile }),
      });
    } catch (err) {
      // Ignore cleanup errors
    }

    setPreview(null);
    setSelectedFile(null);
    setError(null);
    setSessionType('');
    setSelectedTrackId('');
    setVideoFile(null);
    setVideoOffsetH(0);
    setVideoOffsetM(0);
    setVideoOffsetS(0);
    setVideoProgress(0);
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setSessionType('');
    setSelectedTrackId('');
    setVideoFile(null);
    setVideoOffsetH(0);
    setVideoOffsetM(0);
    setVideoOffsetS(0);
    setVideoProgress(0);
  };

  return (
    <div style={styles.page} data-doc-id="import-page">
      {!isEmbedded && (
        <Header
          user={user}
          onLogout={onLogout}
          onBack={onBack}
          showBack={!!onBack}
          onDashboard={onDashboard}
          onAdmin={onAdmin}
          onProfile={onProfile}
        />
      )}
      <div style={styles.content}>
        {!isEmbedded && <div style={styles.title}>IMPORT TELEMETRY</div>}

        {/* Success state */}
        {result && (
          <div style={styles.successCard} data-doc-id="import-success">
            <div style={styles.successTitle}>Session Imported</div>
            <div style={styles.successDetail}>{result.trackName} — {result.lapCount} laps</div>
            <div style={styles.successDetail}>Best Lap: {formatLapTime(result.bestLap)}</div>
            {result.videoError && (
              <div style={{ ...styles.error, marginTop: 12, textAlign: 'left' }}>
                Video upload failed: {result.videoError}. You can re-upload the video from the session page.
              </div>
            )}
            {result.videoSkipped && (
              <div
                data-doc-id="import-result-video-skipped"
                style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  background: 'rgba(126, 200, 227, 0.12)',
                  border: '1px solid rgba(126, 200, 227, 0.3)',
                  borderRadius: 6,
                  color: '#7ec8e3',
                  fontSize: 13,
                  lineHeight: 1.5,
                  textAlign: 'left',
                }}
              >
                Video upload was skipped — this feature is not yet available for your account. The session was imported without the video.
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <button style={styles.uploadBtn} onClick={handleReset}>Import Another</button>
            </div>
          </div>
        )}

        {/* Upload state */}
        {!result && !preview && (
          <>
            <div style={styles.card}>
              <div style={styles.sectionTitle}>TELEMETRY DATA</div>
              <div
                data-doc-id="import-dropzone"
                style={{ ...styles.dropZone, ...(dragActive ? styles.dropZoneActive : {}), ...(uploading ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}
                onClick={() => !uploading && fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => { if (!uploading) handleDrop(e); else e.preventDefault(); }}
              >
                <div style={styles.dropLabel}>
                  {selectedFile ? selectedFile.name : 'Drop a telemetry file here or click to browse'}
                </div>
                <div style={styles.dropHint}>
                  {supportedFormats ? (
                    <>
                      <div style={{ marginBottom: 4 }}>Supported formats:</div>
                      {supportedFormats.map((f, i) => (
                        <div key={i} style={{ fontSize: 12, opacity: 0.85 }}>
                          {f.name} ({f.ext})
                        </div>
                      ))}
                    </>
                  ) : (
                    'Supported formats: AiM CSV, TrackAddict, RaceChrono, RaceBox, MoTeC LD, AiM XRK, VBOX VBO, RaceBox FIT'
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={allowedExtensions.join(',')}
                  multiple={false}
                  style={styles.fileInput}
                  onChange={(e) => { handleFileSelect(e.target.files?.[0]); e.target.value = ''; }}
                />
                {selectedFile && (
                  <div style={styles.selectedFile}>
                    {(selectedFile.size / 1024).toFixed(0)} KB
                  </div>
                )}
              </div>

              {uploading && (
                <div style={{ marginTop: 12 }}>
                  <div style={styles.progress}>
                    {uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Parsing telemetry data...'}
                  </div>
                  <div style={{
                    height: 4, background: 'var(--color-border)', borderRadius: 2, marginTop: 6, overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%', width: `${uploadProgress}%`, background: 'var(--color-accent)',
                      borderRadius: 2, transition: 'width 0.3s',
                    }} />
                  </div>
                </div>
              )}
            </div>

          </>
        )}

        {/* Preview state */}
        {preview && !result && (
          <div data-doc-id="import-preview">
            <SessionTile
              session={{
                track: (preview.availableTracks || []).find((t) => t.trackId === selectedTrackId)?.name || preview.metadata.track,
                trackId: selectedTrackId,
                date: preview.metadata.date,
                sessionType: sessionType,
                deviceType: preview.metadata.sourceFormat || 'import',
                car: preview.metadata.vehicle,
                lapCount: preview.lapCount,
                bestLap: preview.bestLap,
                maxSpeed: preview.maxSpeed || 0,
                weather: preview.weather || null,
                isExample: false,
              }}
              tracks={[]}
              trackThumbMap={selectedTrackId ? { [selectedTrackId]: true } : {}}
              onClick={() => {}}
              onSessionTypeChange={setSessionType}
              onTrackChange={setSelectedTrackId}
              trackOptions={preview.availableTracks || []}
            />

            {preview.importNotes?.length > 0 && (
              <div style={{ ...styles.card, background: '#1a2a3a', borderLeft: '3px solid #4A6A7E' }}>
                <div style={styles.sectionTitle}>IMPORT NOTES</div>
                {preview.importNotes.map((note, i) => (
                  <div key={i} style={{ color: '#8899aa', fontSize: 13, padding: '3px 0' }}>{note}</div>
                ))}
              </div>
            )}

            <div style={styles.card} data-doc-id="import-lap-table">
              <div style={styles.sectionTitle}>LAP TIMES</div>
              <table style={styles.lapTable}>
                <thead>
                  <tr>
                    <th style={styles.lapTh}>Lap</th>
                    <th style={styles.lapTh}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lapTimes.map((time, i) => (
                    <tr key={i}>
                      <td style={styles.lapTd}>{i + 1}</td>
                      <td style={{
                        ...styles.lapTd,
                        ...(time === preview.bestLap ? styles.lapBest : {}),
                      }}>
                        {formatLapTime(time)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={styles.card} data-doc-id="import-channels">
              <div style={styles.sectionTitle}>AVAILABLE DATA CHANNELS</div>
              <div style={styles.fieldsRow}>
                {preview.availableFields.map((f) => (
                  <span key={f} style={styles.fieldTag}>{f}</span>
                ))}
              </div>
            </div>

            <div style={styles.card} data-doc-id="import-video">
              <div style={styles.sectionTitle}>SESSION VIDEO (OPTIONAL)</div>
              <div
                data-doc-id="import-video-dropzone"
                style={{ ...styles.dropZone, ...(videoDragActive ? styles.dropZoneActive : {}), ...(videoUploading ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}
                onClick={() => !videoUploading && videoInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); if (!videoUploading) setVideoDragActive(true); }}
                onDragLeave={() => setVideoDragActive(false)}
                onDrop={(e) => { if (!videoUploading) handleVideoDrop(e); else e.preventDefault(); }}
              >
                <div style={styles.dropLabel}>
                  {videoFile ? videoFile.name : 'Drop a video file here or click to browse'}
                </div>
                <div style={styles.dropHint}>Supported formats: .mp4, .mov</div>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept=".mp4,.mov,video/mp4,video/quicktime"
                  multiple={false}
                  style={styles.fileInput}
                  onChange={(e) => { handleVideoFileSelect(e.target.files?.[0]); e.target.value = ''; }}
                />
                {videoFile && (
                  <div style={styles.selectedFile}>
                    {(videoFile.size / 1024 / 1024).toFixed(1)} MB
                  </div>
                )}
              </div>

              {videoFile && (
                <>
                  <div style={{ ...styles.sectionTitle, marginTop: 20 }}>BEST LAP OFFSET IN VIDEO</div>
                  <div style={styles.offsetRow}>
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={videoOffsetH}
                      onChange={(e) => setVideoOffsetH(Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)))}
                      style={styles.offsetInput}
                      data-doc-id="import-video-offset-h"
                    />
                    <span style={styles.offsetLabel}>h</span>
                    <span style={styles.offsetSep}>:</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={videoOffsetM}
                      onChange={(e) => setVideoOffsetM(Math.max(0, Math.min(59, parseInt(e.target.value, 10) || 0)))}
                      style={styles.offsetInput}
                      data-doc-id="import-video-offset-m"
                    />
                    <span style={styles.offsetLabel}>m</span>
                    <span style={styles.offsetSep}>:</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={videoOffsetS}
                      onChange={(e) => setVideoOffsetS(Math.max(0, Math.min(59, parseInt(e.target.value, 10) || 0)))}
                      style={styles.offsetInput}
                      data-doc-id="import-video-offset-s"
                    />
                    <span style={styles.offsetLabel}>s</span>
                  </div>
                  <div style={styles.offsetHelp}>
                    Time in the video at which the car crosses start/finish completing the best lap ({formatLapTime(preview.bestLap)}).
                  </div>
                </>
              )}

              {videoFile && !allowVideoUpload && (
                <div
                  data-doc-id="import-video-denied"
                  style={{
                    marginTop: 12,
                    padding: '10px 12px',
                    background: 'rgba(126, 200, 227, 0.12)',
                    border: '1px solid rgba(126, 200, 227, 0.3)',
                    borderRadius: 6,
                    color: '#7ec8e3',
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  Video upload is not yet available for your account. You can still import this session — the video file will not be uploaded.
                </div>
              )}

              {videoUploading && (
                <div style={{ marginTop: 12 }}>
                  <div style={styles.progress}>Uploading video... {videoProgress}%</div>
                  <div style={{ height: 4, background: 'var(--color-border)', borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${videoProgress}%`, background: 'var(--color-accent)',
                      borderRadius: 2, transition: 'width 0.3s',
                    }} />
                  </div>
                </div>
              )}
            </div>

            <div style={styles.actionRow} data-doc-id="import-actions">
              <button
                data-doc-id="import-accept"
                style={{
                  ...styles.acceptBtn,
                  ...(accepting || !selectedTrackId || !sessionType || (videoFile && videoSyncOffsetSec <= 0) ? styles.uploadBtnDisabled : {}),
                }}
                onClick={handleAccept}
                disabled={accepting || !selectedTrackId || !sessionType || (videoFile && videoSyncOffsetSec <= 0)}
              >
                {accepting
                  ? 'Saving...'
                  : !selectedTrackId
                  ? 'Select a Track'
                  : !sessionType
                  ? 'Select Session Type'
                  : (videoFile && videoSyncOffsetSec <= 0)
                  ? 'Enter Best Lap Video Offset'
                  : 'Accept Import'}
              </button>
              <button style={styles.rejectBtn} onClick={handleReject} data-doc-id="import-reject">
                Reject
              </button>
            </div>
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}
      </div>
    </div>
  );
}

